import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { objectToFormData } from '../../../../api/utils';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { FACILITY_CENSUS_COLUMNS } from '../../../../reusable-components/datagrid/censusColumns';
import axiosInstance from '../../../../utils/axios';
import CensusConfirmModal from './CensusConfirmModal';
import CensusTableToolbar from './CensusTableToolbar';

CensusModal.propTypes = {
  facilityId: PropTypes.number,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  isDialog: PropTypes.bool,
};

export default function CensusModal({ onClose, onSave, facilityId, isDialog }) {
  const [censusData, setCensusData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changedCensus, setChangedCensus] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updateDefault, setUpdateDefault] = useState(false);
  const [facilitiesUpdated, setFacilitiesUpdated] = useState({});

  const fetchData = () => {
    if (facilityId) {
      setLoading(true);
      axiosInstance.get(`/Admin/FacilityCensus/CensusData?facilityId=${facilityId}`)
        .then(res => setCensusData(res.data ?? {}))
        .catch(() => enqueueSnackbar("Failed to load census data", { variant: "error" }))
        .finally(() => setLoading(false))
    } else setCensusData({});
  }

  useEffect(() => {
    setFacilitiesUpdated({});
    fetchData();
  }, [facilityId]);

  const handleClose = () => { 
    if (onClose) { onClose(); }
    setCensusData({});
    setSaving(false);
    setChangedCensus([]);
    setFacilitiesUpdated({});
  };

  const updateCensus = (budgetDayCount) => {
    setSaving(true);
    axiosInstance.post('/Admin/FacilityCensus/SetFacilityCensus',
      objectToFormData({ dates: changedCensus, fetchDaysForBudgets: budgetDayCount, updateDefaultCensus: updateDefault }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar("Census saved", { variant: "success" });
          if (onSave) onSave(res.data);
          else fetchData();
        } else enqueueSnackbar("Something has gone wrong", { variant: "error" });
      })
      .catch(() => enqueueSnackbar("Something has gone wrong", { variant: "error" }))
      .finally(() => handleClose());
  };

  const columns = useMemo(() => [
    ...FACILITY_CENSUS_COLUMNS,
    {
      field: 'number',
      headerName: 'Amount',
      minWidth: 75,
      flex: 0.5,
      renderCell: (params) =>
        params.row.locked ? <span>{params.row.number}</span> : <TextField
          size="small"
          value={params.row.number}
          autoComplete="off"
          onChange={(e) => {
            const val = Number(e.target.value.replace(/[^0-9]/g, ''));
            if (!Number.isNaN(val)) {
              params.row.number = val;
              params.api.updateRows([params.row]);
              setChangedCensus(prev => {
                const index = prev.findIndex(census => census.date === params.row.date && census.facilityId === params.row.facilityId);
                if (index !== -1) {
                  return prev.map((item, i) => i === index ? { ...item, number: val } : item);
                }
                setFacilitiesUpdated(prev => ({ ...prev, [params.row.facilityId]: params.row.facility }));
              return [...prev, { date: params.row.date, number: val, facilityId: params.row.facilityId }];
              });
            }
          }}
          sx={{
            mt: 0.5,
            mb: 0.5,
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
            '& .MuiFormLabel-root ': { display: 'none' },
          }}
        />,
    },
  ], [censusData]);

  const tableToolbar = useCallback(() => <CensusTableToolbar censusData={censusData} save={updateCensus} saving={saving} facilityId={facilityId} />,
    [censusData, saving, facilityId]);

  const TitleComponent = ({ withClose }) => <DialogTitle sx={{ textAlign: 'center' }}>
    Update Census {withClose && <CloseIconButton onClick={handleClose} />}
  </DialogTitle>;

  const gridHeight = isDialog ? 'calc(100vh - 220px)' : 'calc(100vh - 300px)';

  const BodyComponent = useCallback(() => (
    <CustomDataGrid
      gridId="admin-census-form"
      boxSX={{ width: '100%', height: gridHeight }}
      scrollbarHeight={isDialog ? 100 : 0}
      tableBoxSX={isDialog ? { height: 'calc(100% - 25px)' } : { height: 'calc(100% - 70px)' }}
      data={censusData.censusList ?? []}
      gridColumns={columns}
      sort={[{ field: 'facility', sort: 'asc' }]}
      isLoading={loading}
      CustomLeftToolbar={tableToolbar}
    />
  ), [censusData.censusList, columns, loading, tableToolbar, gridHeight, isDialog]);

  const ButtonComponent = () => {
    if (Object.keys(censusData)?.length === 0 || censusData.locked) return null;

    const actions = <>
      <Button variant="outlined" color="error" size="small" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
        Cancel
      </Button>
      <AwaitButton loading={saving} variant="contained" color="primary" size="small" sx={{ width: 150 }}
        disabled={!changedCensus.length}
        onClick={() => setConfirmOpen(true)
        }>
        Save
      </AwaitButton>
    </>;

    return isDialog
      ? <DialogActions>{actions}</DialogActions>
      : <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0, pb: 0.5 }}>{actions}</Box>;
  };


  return <>
    {
      isDialog ?
        <Dialog open={facilityId > 0} onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }} maxWidth="lg" fullWidth>
          <TitleComponent withClose />
          <DialogContent sx={{ height: '100%', width: '100%' }}>
            <BodyComponent />
          </DialogContent>
          <ButtonComponent />
        </Dialog>
        :
        <Paper sx={{ px: 4 }}>
          <TitleComponent />
          <BodyComponent />
          <ButtonComponent />
        </Paper>
    }
    <CensusConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
      updatedCensus={changedCensus.sort((a, b) => new Date(a.date) - new Date(b.date))}
      facilities={facilitiesUpdated}
      updateData={() => updateCensus(censusData?.budgetDayCount)}
      loading={saving}
      updateDefault={updateDefault} setUpdateDefault={setUpdateDefault}
    />
  </>
}
